import React, { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { StatusBadge } from "../../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function AdminPayments() {
  const { t } = useLang();
  const [payments, setPayments] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = () =>
    API.get("/admin/payments", { params: filter !== "all" ? { status: filter } : {} })
      .then((r) => setPayments(r.data))
      .catch(() => setPayments([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  if (!payments) return <p className="text-slate-500">{t("common.loading")}</p>;

  const act = async (p, action) => {
    if (action === "reject" && !window.confirm(`${t("admin.reject")} ${p.reference}?`)) return;
    try {
      await API.post(`/admin/payments/${p.id}/${action}`);
      toast.success(t(action === "confirm" ? "msg.payment_confirmed" : "msg.payment_rejected"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("nav.payments")}</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="payments-status-filter" className="w-44 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="pending" data-testid="payments-filter-pending">{t("status.pending")}</SelectItem>
            <SelectItem value="confirmed" data-testid="payments-filter-confirmed">{t("status.confirmed")}</SelectItem>
            <SelectItem value="rejected" data-testid="payments-filter-rejected">{t("status.rejected")}</SelectItem>
            <SelectItem value="all" data-testid="payments-filter-all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.shop")}</TableHead>
              <TableHead>{t("landing.plan")}</TableHead>
              <TableHead>{t("common.amount")}</TableHead>
              <TableHead>{t("common.reference")}</TableHead>
              <TableHead>{t("common.date")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {payments.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.reference}`}>
                <TableCell className="font-medium">{p.shop_name}</TableCell>
                <TableCell>{p.plan_name} · {p.billing_cycle}</TableCell>
                <TableCell className="font-mono font-semibold">{money(p.amount)}</TableCell>
                <TableCell className="font-mono text-amber-700">{p.reference}</TableCell>
                <TableCell className="text-sm">{fmtDate(p.created_at)}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right">
                  {p.status === "pending" && (
                    <div className="flex justify-end gap-2">
                      <button data-testid={`confirm-payment-${p.reference}`} onClick={() => act(p, "confirm")}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
                        <Check className="h-3.5 w-3.5" />{t("admin.confirm")}
                      </button>
                      <button data-testid={`reject-payment-${p.reference}`} onClick={() => act(p, "reject")}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                        <X className="h-3.5 w-3.5" />{t("admin.reject")}
                      </button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
